// The POPULATED map view (host + legend + hover tooltip + T31 replay control). Split out of
// `SpatialResultsMapPanel.tsx` so that file stays under the 200-line cap — this is exactly the
// region T31 modifies (mounting the replay control over the map), so the split keeps the new
// control's JSX next to the overlays it must not collide with, rather than growing the panel body.

import { SpatialMapHost } from '../map/SpatialMapLazy';
import type { SpatialMapLayerInput, SpatialMapViewport, SpatialProbeContext, SpatialDrawContract } from '../map/spatialMapTypes';
import { SpatialMapLegend } from './SpatialMapLegend';
import { SpatialMapHoverTooltip } from './SpatialMapHoverTooltip';
import { TemporalReplayControl } from './TemporalReplayControl';
import type { CategoryLegendEntry } from '../map/mapThemes';
import type { HoverContent } from './spatialResultsMapView';
import type { SpatialResultsReplay } from './useSpatialResultsMapReplay';

export function SpatialResultsMapCanvas(props: {
  layers: SpatialMapLayerInput[];
  onFeatureClick: (layerId: string, featureId: string | number) => void;
  onFeatureHover: (layerId: string, featureId: string | number | null, point: { x: number; y: number }) => void;
  selectedFeature: { layerId: string; featureId: string | number } | null;
  onViewportChange?: (viewport: SpatialMapViewport) => void;
  onMapClick?: (lngLat: [number, number]) => void;
  probeContext: SpatialProbeContext | null;
  draw?: SpatialDrawContract | null;
  legend: CategoryLegendEntry[] | null;
  hoverPoint: { x: number; y: number } | null;
  hoverContent: HoverContent;
  replay: SpatialResultsReplay;
}) {
  const { legend, hoverPoint, hoverContent, replay } = props;
  const visibleFeatureIds = replay.timeColumn ? replay.visibleKeys : null;

  return (
    <>
      <SpatialMapHost
        layers={props.layers}
        onFeatureClick={props.onFeatureClick}
        onFeatureHover={props.onFeatureHover}
        selectedFeature={props.selectedFeature}
        onViewportChange={props.onViewportChange}
        onMapClick={props.onMapClick}
        probeContext={props.probeContext}
        draw={props.draw}
        visibleFeatureIds={visibleFeatureIds}
        className="h-full w-full"
      />
      {legend && <SpatialMapLegend entries={legend} />}
      {hoverPoint && (hoverContent.label !== null || hoverContent.props.length > 0) && (
        <SpatialMapHoverTooltip
          label={hoverContent.label}
          properties={hoverContent.props}
          x={hoverPoint.x}
          y={hoverPoint.y}
        />
      )}
      {replay.timeColumn && replay.range && (
        <TemporalReplayControl
          range={replay.range}
          cursor={replay.cursor}
          playing={replay.playing}
          speed={replay.speed}
          onPlay={replay.onPlay}
          onPause={replay.onPause}
          onSeek={replay.seek}
          onSpeedChange={replay.setSpeed}
        />
      )}
    </>
  );
}
